import { Component, Inject, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroupDirective, NgForm } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import type { IFormattedTransaction } from '../../models';
import {
  Alignment,
  ConfirmationService,
  FormBuilder,
  FormData,
  FormValidation,
  FormField,
  InputType,
  MonekColour,
  NotificationService,
  Size,
  VTService,
  ValidationPattern,
  MerchantService,
  ErrorReferenceService,
} from '@odin/odin-core';
import { VTPaymentResponse } from '../../models/vt-payment';
import { CurrencyPipe } from '@angular/common';
import {TransactionComplete} from "../../models/transaction-complete";

export class CompleteModalData {
  constructor(public transaction: IFormattedTransaction) {}
}

/** Error when invalid control is dirty, touched, or submitted. */
export class AmountErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(
    control: FormControl | null,
    form: FormGroupDirective | NgForm | null,
  ): boolean {
    const isSubmitted = form && form.submitted;
    return !!(
      control &&
      control.invalid &&
      (control.dirty || control.touched || isSubmitted)
    );
  }
}

@Component({
  standalone: false,
  selector: 'odin-complete-transaction-modal',
  templateUrl: './complete-transaction-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./complete-transaction-modal.component.scss'],
})
export class CompleteTransactionModalComponent {
  private formattedTransaction: IFormattedTransaction;
  public completeForm: FormData<TransactionComplete>;
  public amountMatcher = new AmountErrorStateMatcher();
  public processing: boolean = false;
  public maxCompletionAmount: number;

  constructor(
    private merchantService: MerchantService,
    public dialogRef: MatDialogRef<CompleteTransactionModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CompleteModalData,
    private VTService: VTService,
    private notifcationService: NotificationService,
    private errorReference: ErrorReferenceService,
    private confirmationService: ConfirmationService,
    private currencyPipe: CurrencyPipe,
  ) {
    this.formattedTransaction = this.data.transaction;
    this.maxCompletionAmount = Math.round((this.formattedTransaction.amount * 1.1) * 100) / 100; // add 10% allowance
    this.completeForm = this.BuildForm();
  }

  private BuildForm(): FormData<TransactionComplete> {
    const userProfile = this.merchantService.getUserprofile();
    const formData = new FormBuilder<TransactionComplete>(MonekColour.Primary)
      .ToggleSubmitButton(true)
      .AddFormField(
        'OperatorId',
        '',
        InputType.Hidden,
        userProfile?.userId || '',
        Size.Full,
      )
      .AddFormField(
        'OperatorName',
        '',
        InputType.Hidden,
        userProfile?.email || '',
        Size.Full,
      )
      .SubmitButtonConfig('Complete', Size.Full, Alignment.Center)
      .AddFormField(
        'PaymentChannel',
        '',
        InputType.Hidden,
        this.formattedTransaction.channel,
        Size.Auto,
      )
      .AddFormField('AuxiliaryData', '', InputType.Hidden, '', Size.Auto)
      .AddFormField('MerchantID', '', InputType.Hidden, '', Size.Auto)
      .AddFormField('CountryCode', '', InputType.Hidden, this.formattedTransaction.merchantCountry, Size.Auto)
      .AddFormField('CurrencyCode', '', InputType.Hidden, this.formattedTransaction.currencyCode, Size.Auto)
      .AddFormField('ValidityID', '', InputType.Hidden, null, Size.Auto)
      .AddFormField(
        'AuthorisationCode',
        '',
        InputType.Hidden,
        // New detail page serves it on the transaction (BFF/TX360); the legacy page on transactionResult (Odin.Transactions/Thor).
        this.formattedTransaction.authorisationCode || this.formattedTransaction.transactionResult?.authorisationCode || '',
        Size.Auto,
      )
      .AddFormField(
        'CrossReference',
        '',
        InputType.Hidden,
        this.formattedTransaction.crossReference,
        Size.Auto,
      )
      .AddFormField(
        'Amount',
        `Amount (${this.currencyPipe.transform(
          this.maxCompletionAmount,
          this.formattedTransaction.currencyCountry,
        )})`,
        InputType.Number,
        null,
        Size.Full,
        new FormValidation()
          .Required('Amount is required')
          .MinValue(
            0.01,
            `Amount must be greater than ${this.currencyPipe.transform(
              0.01,
              this.formattedTransaction.currencyCountry,
            )}`,
          )
          .MaxValue(this.maxCompletionAmount, `Amount can not exceed ${this.currencyPipe.transform(
            this.maxCompletionAmount,
            this.formattedTransaction.currencyCountry,
          )}`)
          .MaxLength(9)
          .PatternMatch(ValidationPattern.Decimal, 'Amount must be a number'),
        true,
        {},
        undefined,
        (formfield: FormField, value: string) => {
          if (value !== '') {
            const numericValue = parseFloat(value);
            if (isNaN(numericValue)) {
              formfield.SetValue('0.00');
            } else {
              formfield.SetValue(numericValue.toFixed(2));
            }
          }
        },
      )
      .Build((data: TransactionComplete) => {
        this.makeCompletion(data);
      });
    return formData;
  }

  private makeCompletion(data: TransactionComplete): void {
    this.processing = true;

    this.confirmationService
      .ConfirmDecline(
        'Are you sure to process this transaction?',
        'By selecting “Yes” the transaction will be completed using the original transaction details, please select to confirm.',
        'Yes',
        'Cancel',
        'primary',
        'warn',
      )
      .subscribe((resp: boolean) => {
        if (resp) this.executeCompletion(data);
        else this.processing = false;
      });
  }
  private executeCompletion(data: TransactionComplete): void {
    this.VTService.SubmitCompleteRequest(data).subscribe(
      (_resp: VTPaymentResponse) => {
        if (_resp.status) {
          this.dialogRef.close(null);
          this.notifcationService.SmallDialog(
            'Transaction completed',
            'Close',
            60000,
          );
        } else {
          const failure = new Error(_resp.message || 'Transaction failed');
          // Keep the server's trace id as the reference; a locally minted one would match no log line.
          this.errorReference.recordBackendReference(failure, _resp.traceId);
          this.completeForm.ShowError(this.errorReference.formatMessage(failure.message, failure));
          this.notifcationService.ErrorSmallDialog('Transaction failed', failure);
        }
        this.processing = false;
      },
      (err: unknown) => {
        this.notifcationService.ErrorSmallDialog('Something went wrong', err);
        this.completeForm.ShowError(this.errorReference.formatMessage('Something went wrong.', err));
        this.processing = false;
      },
    );
  }

  // close without saving
  public cancel(): void {
    this.dialogRef.close(null);
  }
}
